import { FilterStatusEnum, TFilterStatusEnum } from '~/utils/Enum';

/** Minimal shape the filter needs; `status` holds a `FilterStatusEnum` value. */
type TJobWithStatus = { status: string; id?: string };

/**
 * Drops entries sharing a job `id`, keeping the first occurrence.
 *
 * The reducer flattens six overlapping category arrays into one per-day list
 * (see `jobReducer` `GET_ALL_JOBS_REQUEST`), spreading `todayDates` first. A shift
 * that is filled and dated today legitimately appears in both `todayDates` and
 * `confirmedDates`, so it lands here twice — once `TODAY`, once `SCHEDULED`.
 * Keeping the first occurrence collapses it to a single `TODAY` row (GOTU-5814).
 * Entries without an `id` are always kept.
 */
const dedupeById = <T extends TJobWithStatus>(objects: T[]): T[] => {
  const seen = new Set<string>();
  return objects.filter((obj) => {
    if (obj.id == null) return true;
    if (seen.has(obj.id)) return false;
    seen.add(obj.id);
    return true;
  });
};

/**
 * Narrows the calendar's per-day job buckets to the currently selected filter.
 *
 * The calendar fetches every status for the visible month (see `/jobs/count-and-dates`)
 * and filters client-side here — unlike the list view, which filters server-side.
 *
 * @param jobs   Jobs keyed by `YYYY-MM-DD`.
 * @param status A `FilterStatusEnum` value.
 * @returns The same shape, filtered.
 */
export const getFilteredJobs = <T extends TJobWithStatus>(
  jobs: Record<string, T[]>,
  status: TFilterStatusEnum,
): Record<string, T[]> => {
  let statusToFilter: TFilterStatusEnum = status;

  // "All Upcoming": show shifts still ahead of the office — open, confirm-candidate,
  // scheduled, and today — while hiding completed (past) and cancelled/expired shifts.
  // This is the only branch that combines buckets, so it is the only one that can
  // surface the same job twice; de-dupe by id here (GOTU-5814). Single-status filters
  // below must NOT de-dupe — a filled-today shift should still show under "Scheduled".
  if (status === FilterStatusEnum.UPCOMING) {
    return Object.fromEntries(
      Object.entries(jobs).map(([date, objects]) => [
        date,
        dedupeById(
          objects.filter(
            (obj) =>
              obj.status !== FilterStatusEnum.COMPLETED &&
              obj.status !== FilterStatusEnum.CANCELLED_OR_EXPIRED,
          ),
        ),
      ]),
    );
  }

  if (status === FilterStatusEnum.PENDING_COUNTER) {
    statusToFilter = FilterStatusEnum.PENDING;
  }

  return Object.fromEntries(
    Object.entries(jobs).map(([date, objects]) => [
      date,
      objects.filter((obj) => obj.status === statusToFilter),
    ]),
  );
};
